/* One place that knows whether a View Transition is currently running.
 *
 * The Home -> Workspace hand-off morphs the composer between the two pages, and a
 * ::view-transition-group animates position AND size — which is main-thread work, not a
 * compositor-only transform. So anything expensive that lands during those ~400ms competes
 * with the morph directly, and the morph is what loses: the Workspace mounts, fires its
 * fetches, sets state for a grid of video cards and starts decoding their thumbnails, all
 * while the browser is trying to animate two snapshots over the top. The result read as a
 * stuttering slide rather than a hand-off.
 *
 * So: pages ask `afterViewTransition()` and do their loading once the morph has finished.
 * Nothing here delays anything when no transition is running — the promise is already
 * resolved — so a direct visit to the page is unaffected.
 */

let pending = null;

// Resolves when the current transition finishes, or immediately if there is none.
export const afterViewTransition = () => pending || Promise.resolve();

/* Run `update` inside a View Transition when the browser supports one and the visitor has
 * not asked for reduced motion. Returns the same promise either way, so callers do not have
 * to branch. `update` is called synchronously inside the transition — it must have committed
 * its DOM change by the time it returns, which is why callers wrap it in flushSync. */
export const runViewTransition = (update) => {
  const supported = typeof document.startViewTransition === "function"
    && !window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (!supported) {
    update();
    return Promise.resolve();
  }

  const transition = document.startViewTransition(update);
  // `finished` also rejects if the transition is skipped (a duplicate view-transition-name is
  // the usual cause). A skipped transition still needs the gate released, hence the catch.
  pending = transition.finished.catch(() => {}).then(() => { pending = null; });
  return pending;
};
